import type { CustomInterrogationListItem } from '@frontend/api';

import type { RootState } from 'store/configureStore';

import { types, type GeometricInterrogationAction } from './actions';
import type {
  InterrogationMaterialQuery,
  InterrogationSelection,
} from './types';

type GeometricInterrogationState = {
  isEditingInterrogationSetup: boolean;
  isFetching: boolean;
  query: {
    material: InterrogationMaterialQuery | null;
    task: string | null;
  };
  searchResults: CustomInterrogationListItem[];
  selectedResult: InterrogationSelection | null;
};

const initialState: GeometricInterrogationState = {
  isEditingInterrogationSetup: false,
  isFetching: false,
  query: {
    material: null,
    task: null,
  },
  searchResults: [],
  selectedResult: null,
};

const reducer = (
  state: GeometricInterrogationState = initialState,
  action: GeometricInterrogationAction,
): GeometricInterrogationState => {
  switch (action.type) {
    case types.CANCEL_EDIT_INTERROGATION_SETUP:
      return {
        ...state,
        isEditingInterrogationSetup: false,
      };
    case types.CLEAR:
      return {
        ...state,
        query: initialState.query,
        searchResults: [],
        selectedResult: null,
      };
    case types.EDIT_INTERROGATION_SETUP:
      return {
        ...state,
        isEditingInterrogationSetup: true,
      };
    case types.RECEIVE_QUERY: {
      const { material, task } = action.payload;
      return {
        ...state,
        isFetching: true,
        query: {
          material,
          task,
        },
      };
    }
    case types.RECEIVE_RESULT_FOR_DEFAULT: {
      const { result: selectedResult } = action.payload;
      return {
        ...state,
        selectedResult,
      };
    }
    case types.RECEIVE_SEARCH_RESULTS: {
      const { results: searchResults } = action.payload;
      return {
        ...state,
        isFetching: false,
        searchResults,
      };
    }
    case types.SUBMIT: {
      const { result: selectedResult } = action.payload;
      return {
        ...state,
        isEditingInterrogationSetup: false,
        selectedResult,
      };
    }
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): GeometricInterrogationState =>
  state.modules.viewerPage.app.geometricInterrogation;

export const getIsEditingInterrogationSetup = (state: RootState) =>
  getScope(state).isEditingInterrogationSetup;

export const getIsFetching = (state: RootState) => getScope(state).isFetching;

export const getQuery = (state: RootState) => getScope(state).query;

export const getSearchResults = (state: RootState) =>
  getScope(state).searchResults;

export const getSelectedResult = (state: RootState) =>
  getScope(state).selectedResult;
